import React from 'react'

// The clock behind a scene told in steps: the Traceability scene of the home
// page, the How-it-works scene of the Technology page.
//
// Steps are numbered from 1. Each one holds for its own time, then hands
// over to the next; after the last, the scene fades out, jumps back to the
// first step with every transition switched off for one paint, and fades in,
// so nothing flies backwards across it. The loop runs only while the scene is
// on screen. A visitor who has asked for less motion gets the last step,
// whole, and the buttons to look at the others.
//
// The scene puts is-stage-N, is-fading and is-snap on its root from what this
// returns; its stylesheet does the rest.

export interface StagePlayer {
	///@brief   The step on screen, from 1 to the number of durations given.
	stage: number
	///@brief   Fading out before the jump back to the first step.
	bFading: boolean
	///@brief   Transitions off for this paint, so a jump lands at once.
	bSnap: boolean
	///@brief   Whether the scene is on screen, which is when the clock runs.
	bInView: boolean
	bReducedMotion: boolean
	///@brief   For the scene's root: what is watched for being on screen.
	rootRef: React.RefObject<HTMLDivElement | null>
	///@brief   Jump to a step: forwards, everything on the way animates to
	///         where the step puts it; backwards, the step before it is
	///         landed on at once so the step asked for makes its own
	///         entrance; to the first step, through the fade, like the loop.
	goTo: ( target: number ) => void
}

export function useStagePlayer( durationsMs: number[], wrapFadeMs: number ): StagePlayer {

	const count: number = durationsMs.length

	const [ bReducedMotion ] = React.useState<boolean>( (): boolean =>
		typeof window.matchMedia === 'function' && window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches
	)

	const [ stage, setStage ] = React.useState<number>( bReducedMotion ? count : 1 )
	const [ bFading, setFading ] = React.useState<boolean>( false )
	const [ bSnap, setSnap ] = React.useState<boolean>( false )
	const [ bInView, setInView ] = React.useState<boolean>( false )

	// A step asked for while a snap is in flight: played once the snap has
	// released, so its own entrance animates from the step before it.
	const pendingRef = React.useRef<number | null>( null )
	const rootRef = React.useRef<HTMLDivElement>( null )

	// The loop only runs while the scene is on screen.
	React.useEffect( (): ( () => void ) => {
		const root: HTMLDivElement | null = rootRef.current
		if ( root === null || typeof IntersectionObserver !== 'function' ) return (): void => {}
		const observer = new IntersectionObserver( ( entries: IntersectionObserverEntry[] ): void => {
			setInView( entries[ 0 ].isIntersecting )
		}, { threshold: 0.25 } )
		observer.observe( root )
		return (): void => observer.disconnect()
	}, [] )

	// The clock: each step hands over to the next when its time is up, and
	// the last one asks for the fade that brings the first one back.
	React.useEffect( (): ( () => void ) => {
		if ( bReducedMotion || !bInView || bFading || bSnap ) return (): void => {}
		const timer: number = window.setTimeout( (): void => {
			if ( stage < count ) {
				setStage( stage + 1 )
			} else {
				setFading( true )
			}
		}, durationsMs[ stage - 1 ] )
		return (): void => window.clearTimeout( timer )
	}, [ stage, bInView, bFading, bSnap, bReducedMotion, count, durationsMs ] )

	// Once faded out, back to the first step with no transition, then fade in.
	React.useEffect( (): ( () => void ) => {
		if ( !bFading ) return (): void => {}
		const timer: number = window.setTimeout( (): void => {
			setSnap( true )
			setStage( 1 )
			setFading( false )
		}, wrapFadeMs )
		return (): void => window.clearTimeout( timer )
	}, [ bFading, wrapFadeMs ] )

	// Release the snap after the browser has painted the snapped state: one
	// frame to apply it, one more to lift it, so the styles that follow
	// transition from there rather than from where things were.
	React.useLayoutEffect( (): ( () => void ) => {
		if ( !bSnap ) return (): void => {}
		let second: number = 0
		const first: number = window.requestAnimationFrame( (): void => {
			second = window.requestAnimationFrame( (): void => {
				setSnap( false )
				if ( pendingRef.current !== null ) {
					setStage( pendingRef.current )
					pendingRef.current = null
				}
			} )
		} )
		return (): void => {
			window.cancelAnimationFrame( first )
			window.cancelAnimationFrame( second )
		}
	}, [ bSnap ] )

	const goTo = ( target: number ): void => {
		setFading( false )
		pendingRef.current = null

		if ( target === stage ) return

		if ( target > stage ) {
			setStage( target )
			return
		}

		if ( target === 1 ) {
			setFading( true )
			return
		}

		pendingRef.current = target
		setSnap( true )
		setStage( target - 1 )
	}

	return { stage, bFading, bSnap, bInView, bReducedMotion, rootRef, goTo }
}
